import { getSupabaseClient } from '../lib/supabase';
import { Seeker, SeekerListParams, SeekerListResult, SeekerStatus, OnboardingStatus } from "../types/seeker";

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const NEW_THRESHOLD_DAYS = 14;
const INACTIVE_THRESHOLD_DAYS = 90;

function getEnv(): any {
    return (window as any).__ENV__;
}

function getSB() {
    return getSupabaseClient();
}

function deriveStatus(createdAt: string | null): SeekerStatus {
    if (!createdAt) return "new";
    const createdTime = new Date(createdAt).getTime();
    if (Number.isNaN(createdTime)) return "new";

    const ageInDays = (Date.now() - createdTime) / DAY_IN_MS;
    if (ageInDays < NEW_THRESHOLD_DAYS) return "new";
    if (ageInDays >= INACTIVE_THRESHOLD_DAYS) return "inactive";
    return "active";
}

/**
 * GCS-214: derive the most specific lifecycle status for the admin Seekers
 * onboarding column. Precedence is highest-value → lowest so that (e.g.) a
 * seeker whose package is complete is never shown as just "consultation scheduled".
 *
 * Pure derivation from existing columns — no new enums or DB columns.
 */
export function deriveOnboardingStatus(args: {
    rawStatus: OnboardingStatus;
    consultOutcome: 'scheduled' | 'completed' | 'missed' | undefined;
    hasConsultationRecord: boolean;
    hasOpenOffer: boolean;
    latestOrderStatus: 'paid_pending_delivery' | 'delivered' | undefined;
    unitCounts: { total: number; fulfilled: number } | undefined;
}): OnboardingStatus {
    const { rawStatus, consultOutcome, hasConsultationRecord, hasOpenOffer, latestOrderStatus, unitCounts } = args;

    // 1. Package lifecycle states (highest precedence — once money has moved,
    //    nothing about onboarding or consultations matters for display).
    if (unitCounts && unitCounts.total > 0 && unitCounts.fulfilled === unitCounts.total) {
        return 'package_complete';
    }
    if (latestOrderStatus === 'delivered') {
        return 'package_delivered';
    }
    if (latestOrderStatus === 'paid_pending_delivery') {
        return 'package_purchased';
    }

    // 2. Consultation → offer states. If the seeker has completed their consult
    //    and the guide has sent (but not yet closed) a package offer, we call
    //    that "awaiting_package". If no offer yet, just "consultation_completed".
    if (consultOutcome === 'completed') {
        if (hasOpenOffer) return 'awaiting_package';
        return 'consultation_completed';
    }

    // 3. Missed / no_show consult (and no successful later consult — the
    //    ordering in consultOutcome already guarantees "latest wins").
    if (consultOutcome === 'missed') {
        return 'consultation_missed';
    }

    // 4. Existing behavior: consultation scheduled overrides "completed" onboarding.
    if (rawStatus === 'completed' && hasConsultationRecord && consultOutcome === 'scheduled') {
        return 'consultation_scheduled';
    }

    // 5. Fall through to the raw onboarding_status column.
    return rawStatus;
}

export async function listSeekers(params: SeekerListParams = {}): Promise<SeekerListResult> {
    const sb = getSB();
    const {
        q,
        status = "all",
        onboardingStatus = "all",
        lifetimeValueMin,
        lifetimeValueMax,
        page = 1,
        pageSize = 15,
        sortBy = "joinedAt",
        sortDirection = "desc",
    } = params;

    let query = sb
        .from("seeker_onboarding_complete")
        .select(`
            seeker_id,
            session_id,
            seeker_email,
            seeker_over_18,
            location_postcode,
            seeker_gender,
            seeker_full_name,
            seeker_phone,
            experience_types,
            medicine_options_comfort,
            support_types,
            travel_willingness,
            travel_distance,
            focus_preferences,
            prefer_specialist_in_groups,
            religion_alignment_important,
            seeker_beliefs,
            currently_on_meds,
            clinical_diagnoses,
            budget_band,
            additional_info,
            onboarding_started_at,
            onboarding_completed_at,
            onboarding_status,
            latest_match_id
        `, { count: "exact" });

    // Apply sorting (only for fields that exist in the database)
    // connectedGuides and lifetimeValue will be sorted client-side after computation
    const sortableFields = ["name", "onboardingStatus", "joinedAt"];
    if (sortableFields.includes(sortBy)) {
        const columnMap: Record<string, string> = {
            name: "seeker_full_name",
            onboardingStatus: "onboarding_status",
            joinedAt: "onboarding_started_at"
        };
        const column = columnMap[sortBy];
        query = query.order(column, { ascending: sortDirection === "asc", nullsFirst: false });
    } else {
        // Default sort for computed fields - will be sorted client-side later
        query = query.order("onboarding_started_at", { ascending: false });
    }

    if (q && q.trim()) {
        const s = q.trim();
        query = query.or(`seeker_email.ilike.%${s}%,seeker_full_name.ilike.%${s}%,seeker_phone.ilike.%${s}%`);
    }

    // GCS-214: derived post-onboarding statuses all require the underlying
    // onboarding_status to be "completed". We narrow at the DB layer and then
    // apply the exact filter client-side after deriveOnboardingStatus() runs.
    const DERIVED_POST_ONBOARDING_STATUSES: OnboardingStatus[] = [
        "consultation_scheduled",
        "consultation_completed",
        "consultation_missed",
        "awaiting_package",
        "package_purchased",
        "package_delivered",
        "package_complete",
    ];

    if (onboardingStatus !== "all") {
        if (DERIVED_POST_ONBOARDING_STATUSES.includes(onboardingStatus as OnboardingStatus)) {
            // All derived post-onboarding states live on seekers whose raw
            // onboarding_status is "completed". Narrow at the DB level; the
            // exact client-side filter happens after derivation.
            query = query.eq("onboarding_status", "completed");
        } else {
            query = query.eq("onboarding_status", onboardingStatus);
        }
    }

    // Filter by derived status based on onboarding_started_at
    if (status !== "all") {
        const now = Date.now();
        const newCutoff = new Date(now - NEW_THRESHOLD_DAYS * DAY_IN_MS).toISOString();
        const inactiveCutoff = new Date(now - INACTIVE_THRESHOLD_DAYS * DAY_IN_MS).toISOString();

        if (status === "new") {
            query = query.gte("onboarding_started_at", newCutoff);
        } else if (status === "active") {
            query = query.lt("onboarding_started_at", newCutoff).gte("onboarding_started_at", inactiveCutoff);
        } else if (status === "inactive") {
            query = query.lt("onboarding_started_at", inactiveCutoff);
        } else if (status === "no_match") {
            // For no_match, filter to completed onboarding at DB level
            // The final no_match filtering (no active match) is done client-side after fetching match data
            query = query.eq("onboarding_status", "completed");
        }
    }

    // Lifetime value filters (placeholder for future revenue integration)
    void lifetimeValueMin;
    void lifetimeValueMax;

    const start = (page - 1) * pageSize;
    const end = start + pageSize - 1;

    const { data, error, count } = await query.range(start, end);
    if (error) throw error;

    const seekerRows = data ?? [];
    const seekerIds = seekerRows
        .map((row: any) => row.seeker_id)
        .filter((value: any): value is string => typeof value === 'string' && value.length > 0);

    const seekerToUser = new Map<string, string>();
    const profileByUser = new Map<string, { full_name?: string | null; phone?: string | null; email?: string | null; userId: string; blocked?: boolean }>();
    const profileByEmail = new Map<string, { full_name?: string | null; phone?: string | null; email?: string | null; userId: string; blocked?: boolean }>();

    if (seekerIds.length > 0) {
        try {
            const { data: mappings, error: mappingError } = await sb
                .from('seekers')
                .select('seeker_id, user_id')
                .in('seeker_id', seekerIds);

            if (mappingError) {
                console.warn('Failed to load seeker mappings', mappingError);
            } else if (mappings) {
                const userIds = Array.from(
                    new Set(
                        mappings
                            .map((row: any) => row.user_id)
                            .filter((value: any): value is string => typeof value === 'string' && value.length > 0)
                    )
                );

                mappings.forEach((row: any) => {
                    if (row.seeker_id && row.user_id) {
                        seekerToUser.set(row.seeker_id, row.user_id);
                    }
                });

                if (userIds.length > 0) {
                    const { data: profileRows, error: profileError } = await sb
                        .from('profiles')
                        .select('id, full_name, phone, email, blocked')
                        .in('id', userIds);

                    if (profileError) {
                        console.warn('Failed to load seeker profiles', profileError);
                    } else if (profileRows) {
                        profileRows.forEach((row: any) => {
                            if (row.id) {
                                const profileRecord = {
                                    full_name: row.full_name ?? null,
                                    phone: row.phone ?? null,
                                    email: row.email ?? null,
                                    userId: row.id as string,
                                    blocked: row.blocked ?? false,
                                };
                                profileByUser.set(row.id, profileRecord);
                                if (row.email) {
                                    profileByEmail.set(String(row.email).toLowerCase(), profileRecord);
                                }
                            }
                        });
                    }
                }
            }
        } catch (profileErr) {
            console.warn('Unexpected error loading seeker profile data', profileErr);
        }
    }

    // Fetch package offers and orders data for all seekers
    const userIds = Array.from(new Set(
        Array.from(seekerToUser.values())
    ));

    // Map: userId → guide_id of current active match (lowest rank = most recent)
    const currentGuideByUser = new Map<string, string>();
    if (seekerIds.length > 0) {
        const { data: matchRows } = await sb
            .from('matches')
            .select('seeker_id, guide_id, rank')
            .in('seeker_id', seekerIds)
            .eq('status', 'active')
            .order('rank', { ascending: true });

        if (matchRows) {
            matchRows.forEach((row: any) => {
                const userId = seekerToUser.get(row.seeker_id);
                if (userId && !currentGuideByUser.has(userId)) {
                    currentGuideByUser.set(userId, row.guide_id);
                }
            });
        }
    }

    const hasAcceptedOffer = new Map<string, boolean>();
    const lifetimeSpend = new Map<string, number>();
    const guideCount = new Map<string, Set<string>>();
    const consultationScheduledAt = new Map<string, string>();

    // GCS-214: Additional per-seeker signals used to derive the post-consultation
    // / package lifecycle statuses. Keyed by userId (same as the existing maps).
    // We keep the highest-value latest outcome per seeker, so precedence ordering
    // in the query + forEach below matters for *time*, not for the status itself —
    // the final precedence is applied when computing the display status.
    type ConsultOutcome = 'scheduled' | 'completed' | 'missed';
    const consultOutcome = new Map<string, ConsultOutcome>();
    // Package offer presence: has the guide delivered an offer (sent or later)?
    const hasOpenOffer = new Map<string, boolean>();
    // Latest order status per seeker (paid_pending_delivery / delivered)
    const latestOrderStatus = new Map<string, 'paid_pending_delivery' | 'delivered'>();
    // Unit fulfillment tracking: total units vs fulfilled units for the most
    // recent accepted offer. If total > 0 and fulfilled == total → package_complete.
    const offerUnitCounts = new Map<string, { total: number; fulfilled: number }>();

    if (userIds.length > 0) {
        try {
            // Fetch all package offers (not just accepted) so we can detect
            // "awaiting_package" (offer sent but not yet purchased).
            const { data: offerRows, error: offerError } = await sb
                .from('package_offers')
                .select('id, seeker_id, status, created_at')
                .in('seeker_id', userIds)
                .order('created_at', { ascending: false });

            // Track the latest accepted offer per seeker so we can count its units
            const latestAcceptedOfferBySeeker = new Map<string, string>();

            if (!offerError && offerRows) {
                offerRows.forEach((row: any) => {
                    if (!row.seeker_id) return;
                    if (row.status === 'accepted') {
                        hasAcceptedOffer.set(row.seeker_id, true);
                        if (!latestAcceptedOfferBySeeker.has(row.seeker_id)) {
                            latestAcceptedOfferBySeeker.set(row.seeker_id, row.id);
                        }
                    }
                    // Any non-terminal offer counts as "open" for awaiting_package detection
                    if (row.status === 'sent' || row.status === 'accepted') {
                        hasOpenOffer.set(row.seeker_id, true);
                    }
                });
            }

            // Count fulfilled vs total units for the latest accepted offer per seeker,
            // so we can distinguish package_delivered from package_complete.
            const acceptedOfferIds = Array.from(latestAcceptedOfferBySeeker.values());
            if (acceptedOfferIds.length > 0) {
                const { data: unitRows, error: unitError } = await sb
                    .from('package_offer_units')
                    .select('offer_id, status')
                    .in('offer_id', acceptedOfferIds);

                if (!unitError && unitRows) {
                    // Map offer_id → seeker_id for quick lookup
                    const offerToSeeker = new Map<string, string>();
                    latestAcceptedOfferBySeeker.forEach((offerId, seekerId) => {
                        offerToSeeker.set(offerId, seekerId);
                    });

                    unitRows.forEach((row: any) => {
                        const seekerId = offerToSeeker.get(row.offer_id);
                        if (!seekerId) return;
                        const bucket = offerUnitCounts.get(seekerId) || { total: 0, fulfilled: 0 };
                        bucket.total += 1;
                        if (row.status === 'fulfilled') {
                            bucket.fulfilled += 1;
                        }
                        offerUnitCounts.set(seekerId, bucket);
                    });
                }
            }

            // Get orders data for lifetime spend, guide count, and latest order status
            const { data: orderRows, error: orderError } = await sb
                .from('orders')
                .select('seeker_id, guide_id, total_cents, status, created_at')
                .in('seeker_id', userIds)
                .in('status', ['paid_pending_delivery', 'delivered'])
                .order('created_at', { ascending: false });

            if (!orderError && orderRows) {
                orderRows.forEach((row: any) => {
                    if (row.seeker_id) {
                        // Add to lifetime spend
                        const currentSpend = lifetimeSpend.get(row.seeker_id) || 0;
                        const orderTotal = typeof row.total_cents === 'number' ? row.total_cents / 100 : 0;
                        lifetimeSpend.set(row.seeker_id, currentSpend + orderTotal);

                        // Track unique guides
                        if (row.guide_id) {
                            const guides = guideCount.get(row.seeker_id) || new Set<string>();
                            guides.add(row.guide_id);
                            guideCount.set(row.seeker_id, guides);
                        }

                        // Track latest order status (orders are DESC-ordered, so first
                        // write wins). `delivered` outranks `paid_pending_delivery`
                        // regardless of time — upgrade if we see it.
                        const existing = latestOrderStatus.get(row.seeker_id);
                        if (!existing) {
                            latestOrderStatus.set(row.seeker_id, row.status);
                        } else if (existing === 'paid_pending_delivery' && row.status === 'delivered') {
                            latestOrderStatus.set(row.seeker_id, 'delivered');
                        }
                    }
                });
            }

            // Fetch ALL consultation sessions (including no_show/cancelled) so we
            // can surface "Consultation Call Missed" as well as scheduled/completed.
            // Order DESC by created_at so the first row we see per seeker is the
            // most recent — that's the one whose outcome we display.
            const { data: consultationRows, error: consultationError } = await sb
                .from('scheduled_sessions')
                .select('seeker_id, guide_id, created_at, session_status')
                .in('seeker_id', userIds)
                .eq('session_type', 'consultation')
                .in('session_status', ['scheduled', 'confirmed', 'completed', 'no_show', 'cancelled'])
                .order('created_at', { ascending: false });

            if (!consultationError && consultationRows) {
                consultationRows.forEach((row: any) => {
                    if (!row.seeker_id) return;

                    // Keep the first consultation we encounter per seeker for
                    // scheduledAt (backwards compat with existing consultation_scheduled).
                    if (!consultationScheduledAt.has(row.seeker_id)) {
                        consultationScheduledAt.set(row.seeker_id, row.created_at);
                    }

                    // Track the outcome of the most recent consultation. A later
                    // completed call overrides an earlier missed one (and we see
                    // the latest first, so we only write if nothing's there yet).
                    if (!consultOutcome.has(row.seeker_id)) {
                        const s = row.session_status;
                        if (s === 'completed') {
                            consultOutcome.set(row.seeker_id, 'completed');
                        } else if (s === 'no_show' || s === 'cancelled') {
                            // Only count missed if from the seeker's current active guide.
                            // A no_show from an invalidated match must not poison the new match.
                            const currentGuide = currentGuideByUser.get(row.seeker_id);
                            if (!currentGuide || row.guide_id === currentGuide) {
                                consultOutcome.set(row.seeker_id, 'missed');
                            }
                        } else {
                            consultOutcome.set(row.seeker_id, 'scheduled');
                        }
                    }
                });
            }
        } catch (err) {
            console.warn('Unexpected error loading package offers and orders', err);
        }
    }

    let items: Seeker[] = seekerRows.map((row: any) => {
        const joinedAt = row.onboarding_started_at ?? new Date().toISOString();
        let userId = row.seeker_id ? seekerToUser.get(row.seeker_id) : undefined;
        let profileInfo = userId ? profileByUser.get(userId) : undefined;
        const derivedEmail = row.seeker_email ?? profileInfo?.email ?? '';
        if (!profileInfo && derivedEmail) {
            profileInfo = profileByEmail.get(derivedEmail.toLowerCase());
            if (profileInfo?.userId && !userId) {
                userId = profileInfo.userId;
            }
        }
        const derivedName = profileInfo?.full_name && profileInfo.full_name.trim().length
            ? profileInfo.full_name
            : row.seeker_full_name && row.seeker_full_name.trim().length
            ? row.seeker_full_name
            : extractNameFromEmail(derivedEmail) ?? '';
        const phone = profileInfo?.phone ?? row.seeker_phone ?? null;

        // Determine status: if they have accepted package offers, they're active
        // If they've completed onboarding but have no match, show no_match
        // Use latest_match_id from the view (matches any status) to detect matches
        let status: SeekerStatus = deriveStatus(joinedAt);
        const onboardingComplete = row.onboarding_status === 'completed';
        const matchExists = !!row.latest_match_id;

        if (userId && hasAcceptedOffer.get(userId)) {
            status = 'active';
        } else if (onboardingComplete && !matchExists) {
            status = 'no_match';
        }

        // Get actual connected guides count and lifetime value
        const connectedGuides = userId && guideCount.has(userId)
            ? guideCount.get(userId)!.size
            : 0;
        const lifetimeValue = userId ? (lifetimeSpend.get(userId) || 0) : 0;

        return {
            id: row.seeker_id ?? "",
            userId,
            name: derivedName,
            email: derivedEmail,
            phone,
            status,
            blocked: profileInfo?.blocked ?? false,
            connectedGuides,
            lifetimeValue,
            joinedAt,

            // Onboarding data
            sessionId: row.session_id,
            seekerOver18: row.seeker_over_18,
            locationPostcode: row.location_postcode,
            seekerGender: row.seeker_gender,
            experienceTypes: row.experience_types,
            medicineOptionsComfort: row.medicine_options_comfort,
            supportTypes: row.support_types,
            travelWillingness: row.travel_willingness,
            travelDistance: row.travel_distance,
            focusPreferences: row.focus_preferences,
            preferSpecialistInGroups: row.prefer_specialist_in_groups,
            religionAlignmentImportant: row.religion_alignment_important,
            seekerBeliefs: row.seeker_beliefs,
            currentlyOnMeds: row.currently_on_meds,
            clinicalDiagnoses: row.clinical_diagnoses,
            budgetBand: row.budget_band,
            additionalInfo: row.additional_info,
            onboardingStartedAt: row.onboarding_started_at,
            onboardingCompletedAt: row.onboarding_completed_at,
            onboardingStatus: deriveOnboardingStatus({
                rawStatus: row.onboarding_status as OnboardingStatus,
                consultOutcome: userId ? consultOutcome.get(userId) : undefined,
                hasConsultationRecord: !!(userId && consultationScheduledAt.has(userId)),
                hasOpenOffer: !!(userId && hasOpenOffer.get(userId)),
                latestOrderStatus: userId ? latestOrderStatus.get(userId) : undefined,
                unitCounts: userId ? offerUnitCounts.get(userId) : undefined,
            }),
            consultationScheduledAt: userId ? consultationScheduledAt.get(userId) : undefined,
        };
    });

    // If filtering by no_match, filter to only seekers with no_match status
    // (completed onboarding but no active match)
    if (status === "no_match") {
        items = items.filter((s) => s.status === "no_match");
    }

    // GCS-214: derived post-onboarding statuses all required onboarding_status
    // = "completed" at the DB level; now narrow exactly to the derived value.
    if (DERIVED_POST_ONBOARDING_STATUSES.includes(onboardingStatus as OnboardingStatus)) {
        items = items.filter((s) => s.onboardingStatus === onboardingStatus);
    }

    // If filtering by completed, exclude any seeker whose derived status has
    // advanced past plain "completed" (consult scheduled, package purchased, etc.)
    if (onboardingStatus === "completed") {
        items = items.filter((s) => s.onboardingStatus === "completed");
    }

    // For computed fields (status, connectedGuides, lifetimeValue), apply client-side sorting
    const computedFields = ["status", "connectedGuides", "lifetimeValue"];
    if (computedFields.includes(sortBy)) {
        items.sort((a, b) => {
            let aVal: any;
            let bVal: any;

            switch (sortBy) {
                case "status":
                    aVal = a.status;
                    bVal = b.status;
                    break;
                case "connectedGuides":
                    aVal = a.connectedGuides;
                    bVal = b.connectedGuides;
                    break;
                case "lifetimeValue":
                    aVal = a.lifetimeValue;
                    bVal = b.lifetimeValue;
                    break;
                default:
                    return 0;
            }

            if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
            if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
            return 0;
        });
    }

    return { items, total: count ?? items.length };
}

function extractNameFromEmail(email: string): string {
    if (!email) return "";
    const localPart = email.split("@")[0];
    return localPart.replace(/[._]/g, " ").replace(/\b\w/g, l => l.toUpperCase());
}

export async function getSeekerStats() {
    const sb = getSB();

    const { data, error, count } = await sb
        .from("seeker_onboarding_complete")
        .select("seeker_id, onboarding_started_at, onboarding_status", { count: "exact" });

    if (error) throw error;

    const seekers = data ?? [];

    // Get seeker -> user mappings
    const seekerIds = seekers
        .map((row: any) => row.seeker_id)
        .filter((value: any): value is string => typeof value === 'string' && value.length > 0);

    const seekerToUser = new Map<string, string>();
    const hasAcceptedOffer = new Set<string>();
    const hasConsultation = new Set<string>();

    if (seekerIds.length > 0) {
        try {
            // Get seeker -> user mappings
            const { data: mappings } = await sb
                .from('seekers')
                .select('seeker_id, user_id')
                .in('seeker_id', seekerIds);

            if (mappings) {
                mappings.forEach((row: any) => {
                    if (row.seeker_id && row.user_id) {
                        seekerToUser.set(row.seeker_id, row.user_id);
                    }
                });

                const userIds = Array.from(new Set(mappings.map((row: any) => row.user_id)));

                // Check for accepted package offers
                if (userIds.length > 0) {
                    const { data: offerRows } = await sb
                        .from('package_offers')
                        .select('seeker_id')
                        .in('seeker_id', userIds)
                        .eq('status', 'accepted');

                    if (offerRows) {
                        offerRows.forEach((row: any) => {
                            if (row.seeker_id) {
                                hasAcceptedOffer.add(row.seeker_id);
                            }
                        });
                    }

                    // Check for scheduled consultations
                    const { data: consultationRows } = await sb
                        .from('scheduled_sessions')
                        .select('seeker_id')
                        .in('seeker_id', userIds)
                        .eq('session_type', 'consultation')
                        .in('session_status', ['scheduled', 'confirmed', 'completed']);

                    if (consultationRows) {
                        consultationRows.forEach((row: any) => {
                            if (row.seeker_id) {
                                hasConsultation.add(row.seeker_id);
                            }
                        });
                    }
                }
            }
        } catch (err) {
            console.warn('Error loading seeker stats data', err);
        }
    }

    const totals = seekers.reduce(
        (acc, row: any) => {
            const userId = row.seeker_id ? seekerToUser.get(row.seeker_id) : undefined;
            let status = deriveStatus(row.onboarding_started_at ?? null);

            // Override status if they have accepted package offers
            if (userId && hasAcceptedOffer.has(userId)) {
                status = 'active';
            }

            if (status === "new") acc.newCount += 1;
            else if (status === "inactive") acc.inactiveCount += 1;
            else acc.activeCount += 1;

            // Count onboarding statuses
            switch (row.onboarding_status) {
                case "completed":
                    if (userId && hasConsultation.has(userId)) {
                        acc.consultationScheduled += 1;
                    } else {
                        acc.completedOnboarding += 1;
                    }
                    break;
                case "experience_preferences_completed":
                    acc.experienceCompleted += 1;
                    break;
                case "basic_info_completed":
                    acc.basicInfoCompleted += 1;
                    break;
                default:
                    acc.inProgress += 1;
            }

            return acc;
        },
        {
            newCount: 0,
            activeCount: 0,
            inactiveCount: 0,
            completedOnboarding: 0,
            consultationScheduled: 0,
            experienceCompleted: 0,
            basicInfoCompleted: 0,
            inProgress: 0
        }
    );

    return {
        total: count ?? seekers.length,
        new: totals.newCount,
        active: totals.activeCount,
        inactive: totals.inactiveCount,
        onboarding: {
            completed: totals.completedOnboarding,
            consultationScheduled: totals.consultationScheduled,
            experienceCompleted: totals.experienceCompleted,
            basicInfoCompleted: totals.basicInfoCompleted,
            inProgress: totals.inProgress,
        }
    };
}
